// components/Menu.js - Completely rebuilt, simple and reliable
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Menu as MenuIcon,
  X,
  Home,
  FolderOpen,
  Building2,
  Gift,
  Plus,
  Star,
  Wallet,
  LogIn
} from 'lucide-react';

function Menu() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const goToProfile = () => {
    navigate('/profile');
    closeMenu();
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      closeMenu();
      navigate('/profile');
    } catch (error) {
      console.error("Error signing in with Google", error);
    }
  };

  return (
    <>
      {/* Force solid background CSS */}
      <style>{`
        .menu-sidebar-solid {
          background-color: #0f172a !important;
          background-image: none !important;
          background: #0f172a !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
        }
        .menu-header-solid {
          background-color: #1e293b !important;
          background-image: none !important;
          background: #1e293b !important;
          backdrop-filter: none !important;
        }
        .menu-profile-solid {
          background-color: #1e293b !important;
          background-image: none !important;
          background: #1e293b !important;
          backdrop-filter: none !important;
        }
        .menu-nav-solid {
          background-color: #0f172a !important;
          background-image: none !important;
          background: #0f172a !important;
        }
      `}</style>

      {/* Hamburger Menu Button */}
      <button
        className="p-2 rounded-full hover:bg-blue-700 transition-colors"
        onClick={toggleMenu}
        aria-label="Menu"
      >
        <MenuIcon className="h-6 w-6" />
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[9998]"
          onClick={closeMenu}
        />
      )}

      {/* Sidebar Menu */}
      <div
        className={`menu-sidebar-solid fixed top-0 left-0 w-80 h-screen z-[9999] transform transition-transform duration-300 ease-in-out shadow-2xl ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          backgroundColor: '#0f172a',
          backgroundImage: 'none',
          opacity: 1
        }}
      >
        {/* Menu Header */}
        <div 
          className="menu-header-solid p-4 flex justify-between items-center"
          style={{
            backgroundColor: '#1e293b',
            backgroundImage: 'none'
          }}
        >
          <span className="text-white font-bold text-xl">Navigation</span>
          <button
            className="text-white hover:text-slate-300 p-1 rounded-md hover:bg-slate-700 transition-colors"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* User Profile Section */}
        {user && (
          <div
            className="menu-profile-solid p-4 cursor-pointer hover:bg-slate-700 transition-colors border-b border-slate-600"
            onClick={goToProfile}
            style={{
              backgroundColor: '#1e293b',
              backgroundImage: 'none'
            }}
          >
            <div className="flex items-center">
              {user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName || "User"}
                  className="w-12 h-12 rounded-full mr-3 border-2 border-white object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-white rounded-full mr-3 flex items-center justify-center text-blue-900 font-bold">
                  {user.displayName ? user.displayName.charAt(0).toUpperCase() : "U"}
                </div>
              )}
              <div>
                <div className="text-white font-medium">{user.displayName || "User"}</div>
                <div className="text-slate-300 text-sm">View Profile</div>
              </div>
            </div>
          </div>
        )}

        {/* Menu Items */}
        <nav className="menu-nav-solid flex-1 py-4">
          <div className="px-4 space-y-2">
            <Link
              to="/"
              className="flex items-center py-3 px-4 rounded-lg text-white hover:bg-slate-700 transition-colors text-lg"
              onClick={closeMenu}
            >
              <Home className="h-5 w-5 mr-3" />
              Home
            </Link>

            <Link
              to="/bycategory"
              className="flex items-center py-3 px-4 rounded-lg text-white hover:bg-slate-700 transition-colors text-lg"
              onClick={closeMenu}
            >
              <FolderOpen className="h-5 w-5 mr-3" />
              By Categories
            </Link>

            <Link
              to="/bybrands"
              className="flex items-center py-3 px-4 rounded-lg text-white hover:bg-slate-700 transition-colors text-lg"
              onClick={closeMenu}
            >
              <Building2 className="h-5 w-5 mr-3" />
              By Brands
            </Link>

            <Link
              to="/welcomebonus"
              className="flex items-center py-3 px-4 rounded-lg text-white hover:bg-slate-700 transition-colors text-lg"
              onClick={closeMenu}
            >
              <Gift className="h-5 w-5 mr-3" />
              Welcome Bonus
            </Link>

            <Link
              to="/create-brand"
              className="flex items-center py-3 px-4 rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors text-lg font-semibold"
              onClick={closeMenu}
            >
              <Plus className="h-5 w-5 mr-3" />
              Create Brand
            </Link>

            {/* User-specific items */}
            {user ? (
              <>
                <Link
                  to="/mybrands"
                  className="flex items-center py-3 px-4 rounded-lg text-white hover:bg-slate-700 transition-colors text-lg"
                  onClick={closeMenu}
                >
                  <Star className="h-5 w-5 mr-3" />
                  My Brands
                </Link>

                <Link
                  to="/transactions"
                  className="flex items-center py-3 px-4 rounded-lg text-white hover:bg-slate-700 transition-colors text-lg"
                  onClick={closeMenu}
                >
                  <Wallet className="h-5 w-5 mr-3" />
                  My Wallet
                </Link>
              </>
            ) : (
              <button
                onClick={handleGoogleSignIn}
                className="w-full flex items-center text-left py-3 px-4 rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors text-lg font-semibold"
              >
                <LogIn className="h-5 w-5 mr-3" />
                Sign In with Google
              </button>
            )}
          </div>
        </nav>


      </div>
    </>
  );
}

export default Menu;